import { useCallback } from "react";

import { Action, Module, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";

import useHasEntityWriteAccess from "./useHasEntityWriteAccess";

const SKIP_ACCESS_TYPES = [Action.SpaceWrite];

const hasSkipExternalDependencyAccess = (viewer: AccountViewer, spaceId?: string) =>
  hasAtLeastOneRequiredAction(SKIP_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can skip an external dependency.
 * A user needs write access to skip an external dependency.
 * @returns Function that takes a dependency and optional stack or module and returns a AccessResult indicating if user can skip it
 */
const useCanSkipExternalDependency = (): ((
  entity?: Partial<Pick<Stack | Module, "space" | "spaceDetails" | "canWrite">>
) => AccessResult) => {
  const hasEntityWriteAccess = useHasEntityWriteAccess();
  const { viewer } = useTypedContext(AccountContext);

  return useCallback(
    (entity) => {
      if (!entity) {
        return { result: false };
      }

      const hasAccess =
        hasEntityWriteAccess(entity) ||
        hasSkipExternalDependencyAccess(viewer, entity.spaceDetails?.id || entity.space);

      if (hasAccess) {
        return { result: true };
      }

      return { result: false, message: NO_ACCESS_MESSAGE };
    },
    [viewer, hasEntityWriteAccess]
  );
};

export default useCanSkipExternalDependency;
